/**
 * Permission utilities for staff management and role-based access control
 * Handles permission parsing, checking, and authorization logic
 */

/**
 * Parse permissions object (normalize null/undefined)
 * @param {Object} permissions - Raw permissions object from user
 * @returns {Object} Normalized permissions object
 */
export const parsePermissions = (permissions) => {
  return permissions || {};
};

/**
 * Check if user has specific permission
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name (e.g., 'lead_management', 'staff_management')
 * @param {string} action - Action name ('read', 'create', 'update', 'delete')
 * @returns {boolean} True if permission exists and is true
 */
export const hasPermission = (permissions, module, action) => {
  if (!permissions || !module || !action) return false;
  return permissions[module]?.[action] === true;
};

/**
 * Get all permissions for a specific module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @returns {Object} Permission object for module { read, create, update, delete }
 */
export const getModulePermissions = (permissions, module) => {
  return permissions?.[module] || { read: false, create: false, update: false, delete: false };
};

/**
 * Check if user has at least one action in a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @returns {boolean} True if user has any permission in module
 */
export const hasAnyModulePermission = (permissions, module) => {
  const modulePerms = getModulePermissions(permissions, module);
  return Object.values(modulePerms).some((perm) => perm === true);
};

/**
 * Get all modules where user has at least one permission
 * @param {Object} permissions - User permissions object
 * @returns {Array} Array of module names
 */
export const getAccessibleModules = (permissions) => {
  if (!permissions) return [];
  return Object.keys(permissions).filter((module) => hasAnyModulePermission(permissions, module));
};

/**
 * Determine if user is admin (has all core permissions)
 * Admin typically has full access to all modules
 * @param {Object} permissions - User permissions object
 * @returns {boolean} True if user appears to be admin
 */
export const isAdmin = (permissions) => {
  if (!permissions) return false;
  // Check if has staff_management permission (usually only admins)
  return hasPermission(permissions, 'staff_management', 'read');
};

/**
 * Filter list of items based on user permissions
 * Only returns items the user has read permission for
 * @param {Array} items - Array of items with module property
 * @param {Object} permissions - User permissions object
 * @returns {Array} Filtered items
 */
export const filterByPermission = (items, permissions) => {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    if (!item.module) return true; // Include items without module requirement
    return hasPermission(permissions, item.module, 'read');
  });
};

const permissionUtils = {
  parsePermissions,
  hasPermission,
  getModulePermissions,
  hasAnyModulePermission,
  getAccessibleModules,
  isAdmin,
  filterByPermission,
};

export default permissionUtils;
